import type { RecognitionScheduleImpact } from "@chro/shared";

const round = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
type Amounts = RecognitionScheduleImpact["totals"];
const families = [["beforeEUR", "changeEUR", "afterEUR"], ["beforeFinalEUR", "changeFinalEUR", "afterFinalEUR"], ["beforeEstimateEUR", "changeEstimateEUR", "afterEstimateEUR"]] as const;

/** Presentation bridge between two authoritative server projections. Never
 * compounds changes: keep the first Before, use the latest After, subtract.
 * A different economic source set cannot be compared to the old baseline. */
export function cumulativeRecognitionImpact(baseline: RecognitionScheduleImpact, latest: RecognitionScheduleImpact): RecognitionScheduleImpact | null {
  if (JSON.stringify([...baseline.sourceItemIds].sort()) !== JSON.stringify([...latest.sourceItemIds].sort())) return null;
  for (const [before] of families) if (baseline.totals[before] !== latest.totals[before]) return null;
  const amounts = (before: Amounts | undefined, after: Amounts | undefined): Amounts => {
    const result = { ...(after ?? before ?? latest.totals) };
    for (const [b, c, a] of families) {
      result[b] = before ? before[b] : baseline.totals[b] === null ? null : 0;
      result[a] = after ? after[a] : latest.totals[a] === null ? null : 0;
      result[c] = result[b] === null || result[a] === null ? null : round(result[a]! - result[b]!);
    }
    return result;
  };
  const before = new Map(baseline.rows.map((row) => [row.month, row]));
  const after = new Map(latest.rows.map((row) => [row.month, row]));
  const rows = [...new Set([...before.keys(), ...after.keys()])].sort().map((month) => ({ ...amounts(before.get(month), after.get(month)), month }));
  const totals = amounts(baseline.totals, latest.totals);
  const roundingDifference = Object.fromEntries(families[0].map((key) => [key, totals[key] === null || rows.some((row) => row[key] === null) ? null : round(rows.reduce((sum, row) => sum + row[key]!, 0) - totals[key]!)])) as RecognitionScheduleImpact["roundingDifference"];
  return { ...latest, rows, totals, roundingDifference };
}
